import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Users, BedDouble, Bath, Wifi, Car, CheckCircle, ArrowRight, X, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';

export default function CaravanPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [caravan, setCaravan] = useState(null);
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [bookedDates, setBookedDates] = useState([]);
  const [priceInfo, setPriceInfo] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [nearby, setNearby] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/caravans/${slug}`),
      api.get(`/caravans/${slug}/availability`),
      api.get('/nearby')
    ]).then(([cRes, aRes, nRes]) => {
      setCaravan(cRes.data);
      const allDates = [...(aRes.data.booked_dates || []), ...(aRes.data.blocked_dates || [])];
      setBookedDates(allDates.map(d => new Date(d)));
      setNearby(nRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (dateRange.from && dateRange.to && caravan) {
      api.get(`/pricing/calculate?caravan_id=${caravan.id}&check_in=${dateRange.from.toISOString().split('T')[0]}&check_out=${dateRange.to.toISOString().split('T')[0]}`)
        .then(r => setPriceInfo(r.data))
        .catch(() => setPriceInfo(null));
    } else {
      setPriceInfo(null);
    }
  }, [dateRange, caravan]);

  const handleBook = () => {
    if (!dateRange.from || !dateRange.to) return;
    const ci = dateRange.from.toISOString().split('T')[0];
    const co = dateRange.to.toISOString().split('T')[0];
    navigate(`/book/${slug}?checkin=${ci}&checkout=${co}`);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B726A]">Loading...</div>;
  if (!caravan) return <div className="min-h-screen flex items-center justify-center text-[#6B726A]">Caravan not found</div>;

  const images = caravan.images || [];

  return (
    <div data-testid="caravan-page">
      {/* Gallery */}
      <section className="py-6 px-6 md:px-12 lg:px-24" data-testid="caravan-gallery">
        <div className="max-w-[1440px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 rounded-xl overflow-hidden">
            {images.map((img, i) => (
              <div key={i} className={`img-zoom-wrapper cursor-pointer ${i === 0 ? 'col-span-2 row-span-2' : ''}`} onClick={() => setLightbox(i)} data-testid={`gallery-img-${i}`}>
                <img src={img.url} alt={img.alt} className={`w-full object-cover ${i === 0 ? 'h-full min-h-[300px]' : 'h-48 md:h-56'}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox !== null && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)} data-testid="lightbox">
          <button className="absolute top-4 right-4 text-white z-10" onClick={() => setLightbox(null)} data-testid="lightbox-close"><X className="w-8 h-8" /></button>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white z-10" onClick={(e) => { e.stopPropagation(); setLightbox(Math.max(0, lightbox - 1)); }}><ChevronLeft className="w-8 h-8" /></button>
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white z-10" onClick={(e) => { e.stopPropagation(); setLightbox(Math.min(images.length - 1, lightbox + 1)); }}><ChevronRight className="w-8 h-8" /></button>
          <img src={images[lightbox]?.url} alt={images[lightbox]?.alt} className="max-w-[90vw] max-h-[85vh] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Details + Booking */}
      <section className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left: Details */}
          <div className="lg:col-span-7 space-y-8">
            <div>
              {caravan.park && (
                <Link to={`/parks/${caravan.park.slug}`} className="text-xs text-[#CC5A3D] font-medium hover:underline" data-testid="caravan-park-link">{caravan.park.name}</Link>
              )}
              <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-medium text-[#1F2A25] mt-2 mb-4" data-testid="caravan-title">{caravan.name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-[#6B726A]">
                <span className="flex items-center gap-1"><Users className="w-4 h-4" /> Sleeps {caravan.sleeps}</span>
                <span className="flex items-center gap-1"><BedDouble className="w-4 h-4" /> {caravan.bedrooms} Bedrooms</span>
                <span className="flex items-center gap-1"><Bath className="w-4 h-4" /> {caravan.bathrooms} Bathrooms</span>
              </div>
            </div>

            <p className="text-base leading-relaxed text-[#6B726A]" data-testid="caravan-description">{caravan.description}</p>

            {/* Features */}
            <div className="flex flex-wrap gap-2">
              {(caravan.features || []).map((f, i) => (
                <Badge key={i} variant="secondary" className="bg-[#F0EBE1] text-[#1E3F33] border-[#E6D5B8]">{f}</Badge>
              ))}
            </div>

            {/* Facilities */}
            <div data-testid="caravan-facilities">
              <h3 className="text-xl font-heading font-medium text-[#1F2A25] mb-4">Facilities</h3>
              <div className="grid grid-cols-2 gap-3">
                {(caravan.facilities || []).map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-[#1F2A25]">
                    <CheckCircle className="w-4 h-4 text-[#2D6A4F] flex-shrink-0" /> {f}
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing */}
            <div data-testid="caravan-pricing">
              <h3 className="text-xl font-heading font-medium text-[#1F2A25] mb-4">Pricing</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-[#E6D5B8] p-4 rounded-lg text-center">
                  <p className="text-xs text-[#6B726A] mb-1">Off-Peak / Night</p>
                  <p className="text-xl font-heading font-medium text-[#1E3F33]">&pound;{caravan.pricing?.off_peak}</p>
                </div>
                <div className="bg-white border border-[#E6D5B8] p-4 rounded-lg text-center">
                  <p className="text-xs text-[#6B726A] mb-1">Peak / Night</p>
                  <p className="text-xl font-heading font-medium text-[#CC5A3D]">&pound;{caravan.pricing?.peak}</p>
                </div>
                <div className="bg-white border border-[#E6D5B8] p-4 rounded-lg text-center">
                  <p className="text-xs text-[#6B726A] mb-1">Off-Peak / Week</p>
                  <p className="text-xl font-heading font-medium text-[#1E3F33]">&pound;{caravan.pricing?.weekly_off_peak}</p>
                </div>
                <div className="bg-white border border-[#E6D5B8] p-4 rounded-lg text-center">
                  <p className="text-xs text-[#6B726A] mb-1">Peak / Week</p>
                  <p className="text-xl font-heading font-medium text-[#CC5A3D]">&pound;{caravan.pricing?.weekly_peak}</p>
                </div>
              </div>
              <p className="text-xs text-[#6B726A] mt-3">Peak: April, June-August, December. Security deposit: &pound;{caravan.deposit_amount || 250}</p>
            </div>
          </div>

          {/* Right: Calendar + Book */}
          <div className="lg:col-span-5">
            <div className="sticky top-24 bg-white border border-[#E6D5B8] p-6 rounded-xl shadow-[0_8px_30px_rgba(30,63,51,0.04)]" data-testid="booking-widget">
              <h3 className="text-xl font-heading font-medium text-[#1F2A25] mb-1">Check Availability</h3>
              <p className="text-sm text-[#6B726A] mb-4">Select your dates to see pricing</p>

              <Calendar
                mode="range"
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={1}
                disabled={[{ before: new Date() }, ...bookedDates]}
                className="rounded-md border border-[#E6D5B8] p-3"
                data-testid="availability-calendar"
              />

              {priceInfo && (
                <div className="mt-4 p-4 bg-[#F0EBE1] rounded-lg" data-testid="price-summary">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#6B726A]">{priceInfo.nights} night{priceInfo.nights > 1 ? 's' : ''}</span>
                    <span className="font-medium text-[#1F2A25]">&pound;{priceInfo.total_price.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#6B726A]">Security deposit</span>
                    <span className="text-[#6B726A]">&pound;{priceInfo.deposit_amount.toFixed(2)} (separate)</span>
                  </div>
                  <div className="border-t border-[#E6D5B8] pt-2 flex justify-between">
                    <span className="font-medium text-[#1F2A25]">Total</span>
                    <span className="text-xl font-heading font-medium text-[#CC5A3D]">&pound;{priceInfo.total_price.toFixed(2)}</span>
                  </div>
                </div>
              )}

              <Button
                onClick={handleBook}
                disabled={!dateRange.from || !dateRange.to}
                className="w-full mt-4 bg-[#CC5A3D] text-[#F9F8F6] hover:bg-[#B34A30] rounded-sm py-3 text-base disabled:opacity-50"
                data-testid="book-now-btn"
              >
                Book Now <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-xs text-center text-[#6B726A] mt-2">You won't be charged yet</p>
            </div>
          </div>
        </div>
      </section>

      {/* What's Nearby */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-[#F0EBE1]" data-testid="caravan-nearby">
        <div className="max-w-[1440px] mx-auto">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-[#1F2A25] mb-8">What's nearby</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {nearby.slice(0, 8).map((item, i) => (
              <div key={i} className="bg-white border border-[#E6D5B8] p-5 rounded-lg">
                <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#CC5A3D]">{item.category}</span>
                <h4 className="text-lg font-heading font-medium text-[#1F2A25] mt-1 mb-1">{item.name}</h4>
                <p className="text-xs text-[#6B726A] line-clamp-2">{item.description}</p>
                <span className="text-xs font-medium text-[#1E3F33] mt-2 block"><MapPin className="w-3 h-3 inline mr-1" />{item.distance}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
